export interface IProfileSettingsForm {
  profileSettingPopUpActive: boolean;
  showBankSelection: 'showMergedLowBank' | 'showLeftRightBanks';
  checked: {
    showFineGrid: boolean;
    previews: boolean;
  };
  input: {
    fineGrid: number;
    lowBankOffset: number;
    sampleInterval: number;
    verticalInterval: number;
  };
}

export const profileSettingsFormDefault: IProfileSettingsForm = {
  profileSettingPopUpActive: false,
  showBankSelection: 'showMergedLowBank',
  checked: {
    showFineGrid: false,
    previews: false,
  },
  input: {
    fineGrid: Math.trunc(25.0),
    lowBankOffset: 20.0,
    sampleInterval: 2.0,
    verticalInterval: 5.0,
  },
};

export interface IWaterwayTableCol {
  name: string;
  title: string;
  editable: boolean;
  editableOnStartAndEnd?: boolean;
  editableOnR0?: boolean;
  trapezoidalOnly?: boolean;
  parabolicOnly?: boolean;
  blankOnR0?: boolean;
  blankOnStartAndEnd?: boolean;
}

export const trapezoidalCols: IWaterwayTableCol[] = [
  { name: 'leftSS', title: 'Left SS\n(ft/ft)', editable: true, trapezoidalOnly: true, blankOnStartAndEnd: true },
  { name: 'bottomWidth', title: 'Bottom Width\n(ft)', editable: true, trapezoidalOnly: true, blankOnStartAndEnd: true },
  { name: 'rightSS', title: 'Right SS\n(ft/ft)', editable: true, trapezoidalOnly: true, blankOnStartAndEnd: true },
];

export const parabolicCols: IWaterwayTableCol[] = [
  { name: 'leftSS', title: 'Limiting Slope\n(ft/ft)', editable: true, parabolicOnly: true, blankOnStartAndEnd: true },
  { name: 'offset1', title: 'Offset 1\n(%)', editable: true, parabolicOnly: true, blankOnStartAndEnd: true },
  { name: 'offset2', title: 'Offset 2\n(%)', editable: true, parabolicOnly: true, blankOnStartAndEnd: true },
  { name: 'offset3', title: 'Offset 3\n(%)', editable: true, parabolicOnly: true, blankOnStartAndEnd: true },
];

export const lengthAndSlope: IWaterwayTableCol[] = [
  { name: 'stationLength', title: 'Length\n(ft)', editable: false, editableOnStartAndEnd: true, blankOnR0: true },
  { name: 'grade', title: 'Slope\n(%)', editable: false, editableOnStartAndEnd: true, blankOnR0: true },
];

export const stationViewBeforeCols: IWaterwayTableCol[] = [
  { name: 'station', title: 'Station\n(ft)', editable: true, editableOnR0: true, blankOnStartAndEnd: true },
  { name: 'groundZ', title: 'Ground Z\n(ft)', editable: false },
  { name: 'channelZ', title: 'Channel Z\n(ft)', editable: true, editableOnR0: true },
  { name: 'cutFill', title: 'Cut/ Fill\n(ft)', editable: false },
];

export const stationViewAfterCols: IWaterwayTableCol[] = [
  { name: 'runoffQ', title: 'Runoff Q\n(cfs)', editable: false, blankOnStartAndEnd: true },
  { name: 'addedQ', title: 'Added Q\n(cfs)', editable: true, editableOnR0: true, blankOnStartAndEnd: true },
  { name: 'totalQ', title: 'Total Q\n(cfs)', editable: false, blankOnStartAndEnd: true },
];

export const reachViewBeforeCols: IWaterwayTableCol[] = [
  { name: 'reachId', title: 'Reach', editable: false },
  { name: 'startStation', title: 'Start\n(ft)', editable: false },
  { name: 'endStation', title: 'End\n(ft)', editable: false },
];

export const grassedWaterwayCols: IWaterwayTableCol[] = [
  { name: 'stabVelocity', title: 'Velocity Stability\n(ft/sec)', editable: false, blankOnStartAndEnd: true },
  { name: 'capaVelocity', title: 'Velocity Capacity\n(ft/sec)', editable: false, blankOnStartAndEnd: true },
  {
    name: 'soilStrength',
    title: 'Soil Strength\n(lb/sq ft)',
    editable: false,
    blankOnR0: true,
    blankOnStartAndEnd: true,
  },
  { name: 'vegCover', title: 'Veg. Cover', editable: false, blankOnR0: true, blankOnStartAndEnd: true },
  { name: 'stability', title: 'Stability', editable: false, blankOnR0: true, blankOnStartAndEnd: true },
  { name: 'capacity', title: 'Capacity', editable: false, blankOnR0: true, blankOnStartAndEnd: true },
];

export const linedWaterwayCols: IWaterwayTableCol[] = [
  { name: 'stabVelocity', title: 'Velocity\n(ft/s)', editable: false, blankOnR0: true, blankOnStartAndEnd: true },
  { name: 'capacity', title: 'Lining Material', editable: false, blankOnR0: true, blankOnStartAndEnd: true },
];

// *** Station View ***
export const stationViewCols: IWaterwayTableCol[] = [
  ...stationViewBeforeCols,
  ...lengthAndSlope,
  ...stationViewAfterCols,
];

export const initialFormValidationReachTable = {
  formErrors: {
    status: '',
    reachId: '',
    startStation: '',
    endStation: '',
    station: '',
    groundZ: '',
    channelZ: '',
    cutFill: '',
    stationLength: '',
    grade: '',
    leftSS: '',
    rightSS: '',
    bottomWidth: '',
    offset1: '',
    offset2: '',
    offset3: '',
    runoffQ: '',
    addedQ: '',
    totalQ: '',
    flowDepthFB: '',
    flowTopWidth: '',
    channelParams: {
      bermWidth: '',
      leftSlopeRatio: '',
      offset2: '',
      offset1: '',
      offset3: '',
      rightSlopeRatio: '',
      minBedWidth: '',
    },
    simDepth: '',
    depth: '',
  },
  statusValid: true,
  reachIdValid: true,
  startStationValid: true,
  endStationValid: true,
  stationValid: true,
  groundZValid: true,
  channelZValid: true,
  cutFillValid: true,
  stationLengthValid: true,
  gradeValid: true,
  leftSSValid: true,
  rightSSValid: true,
  bottomWidthValid: true,
  offset1Valid: true,
  offset2Valid: true,
  offset3Valid: true,
  runoffQValid: true,
  addedQValid: true,
  totalQValid: true,
  flowDepthFBValid: true,
  flowTopWidthValid: true,
  channelParamsValid: {
    bermWidthValid: true,
    leftSlopeRatioValid: true,
    offset2Valid: true,
    offset1Valid: true,
    offset3Valid: true,
    rightSlopeRatioValid: true,
    minBedWidthValid: true,
  },
  simDepthValid: true,
  depthValid: true,
  formValid: true,
  invalidFields: [] as { fieldName: string; index: number }[],
};
